import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@progress/kendo-react-buttons';
import _ from 'lodash';
import AutoComplete from './Autocomplete';
import Grid from '../grid/Grid';
import Dialog from '../dialogs/Dialog';
import MetaGrid from '../meta/Grid';
import eventEmitter from '../utils/eventEmitter';
import withMeta from '../utils/withMeta';
import flags from '../utils/flags';
import { processAfter, isDateType } from '../utils/helper';
import './Picker.scss';

const buildFilter = (selected, textField, required, allDataItem) => {
  if (!selected || (required && allDataItem && allDataItem === selected)) {
    return {
      filters: []
    };
  }

  return {
    logic: 'and',
    filters: [
      {
        field: textField,
        operator: "contains",
        value: _.get(selected, textField.split('.'))
      }
    ]
  }
}

const autocompleteHasValue = (filter, textField) => {
  return filter?.filters && filter.filters.length === 1 && filter.filters[0].field === textField && filter.filters[0].value
}

let _flag;

/**
 * Picker component
 * @module
 * @param {string} name - Name
 * @param {string} title - Picker title
 * @param {string} textField - Text field
 * @param {func} onChange - Function invoked on selected change
 * @param {func} [onSubmit] - Function invoked on sumbit
 * @param {array} data - All data for picker
 * @param {bool} isLoading - Loading
 * @param {func} onSelect - Function invoked on item select
 * @param {object} [selected] - Current selected item
 * @param {number} total - Total number of items
 * @param {array} schema - Schema for picker
 * @param {bool} [required] - Is picker required?
 * @param {bool} [disabled] - Is picker readonly?
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {string} [primaryField] - Primary field
 * @param {func} [onOpened] - Function invoked on picker open
 * @param {object} [initFilter] - Filter, can be canceled
 * @param {object} [fixedFilter] - Filter, can't be canceled
 * @param {bool} [autofocus] - Should picker be focused?
 * @param {string} [placeholder] - Picker placeholder
 * @param {any} [detail] - Detail, create new record (null = without this function)
 * @param {bool} [editableDetail] - Editable detail, requires onSubmit and detail props
 * @param {func} [onAdd] - Function invoked on add entry
 * @param {number} [tabIndex] - Picker tab index
 * @param {bool} [isOpen] - Is picker open?
 * @param {func} [openPicker] - Function invoked on picker open
 * @param {func} [closePicker] - Function invoked on picker close
 * @param {string} [db] - db
 * @param {array} [sort] - Array of sorts for AutoComplete component
 */

const PickerComponent = (props) => {
  const [isOpen, setIsOpen] = useState(props.isOpen)
  const [filter, setFilter] = useState(props.initFilter || { filters: [] })
  const [selected, setSelected] = useState(null)
  const [fixedFilter, setFixedFilter] = useState(props.fixedFilter)

  const init = useCallback(async () => {
    if (props.withMeta) {
      const f = await props.meta.getFilters(props.identif || props.primaryField, props.schema, null, props.db);
      setFixedFilter({ filters: [...(props.fixedFilter ? props.fixedFilter.filters : []), ...f.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat()] });
      setFilter(prev => props.initFilter || { filters: [...(prev?.filters ?? []), ...f.filter(x => !x.disabled && x.CanDisabled).map(x => x.filters).flat()] })
    } else {
      setFixedFilter(props.fixedFilter);
      const newFilter = _.get(props.selected, props.textField.split('.')) ? buildFilter(props.selected, props.textField, props.required, props.allDataItem) : props.initFilter;
      setFilter(newFilter);
    }
    // eslint-disable-next-line
  }, [props.primaryField, props.db, props.identif, props.textField, props.required, props.withMeta, JSON.stringify(props.initFilter), JSON.stringify(props.fixedFilter), JSON.stringify(props.schema), JSON.stringify(props.selected), JSON.stringify(props.allDataItem)])

  useEffect(() => {
    init();
  }, [init]);

  // nahradza povodny getDerivedStateFromProps()
  useEffect(() => {
    if (
      !_flag &&
      selected !== props.selected &&
      !(autocompleteHasValue(filter, props.textField) && props.selected && selected === null)
    ) {
      const f = buildFilter(props.selected, props.textField, props.required, props.allDataItem);
      setFilter(f);
      setSelected(props.selected);
    }
    // eslint-disable-next-line
  }, [JSON.stringify(props.selected), JSON.stringify(filter), JSON.stringify(props.allDataItem), props.textField, props.required]);

  useEffect(() => {
    if (!isOpen && !_.isEqual(selected, props.selected)) handleBlur();
    // eslint-disable-next-line
  }, [JSON.stringify(props.selected)]);


  const handleOpen = (event) => {
    event.preventDefault();
    setIsOpen(true);
    if (props.initFilter && filter.filters.length === 0) {
      setFilter(props.initFilter);
    }
    if (props.openPicker !== undefined) {
      props.openPicker();
    }

    if (props.onOpened) {
      props.onOpened();
    }
  };

  const handleClose = () => {
    _flag = false;
    if (props.closePicker !== undefined) {
      props.closePicker();
    }
    setIsOpen(false);
  };

  const handleFilter = (filter) => {
    setFilter(filter);
  };

  const handleSelect = (selected) => {
    _flag = true;
    setSelected(selected[0]);
  };

  const handleConfirm = async () => {
    _flag = false;
    const f = buildFilter(selected, props.textField, props.required, props.allDataItem);
    setFilter(f);
    setIsOpen(false);
    await props.onSelect(props.name, selected);
  };

  const handleSelectAndConfirm = async (selected, name, e) => {
    const item = props.required && props.allDataItem && !selected && e.nativeEvent.type === "click" && e.target.value === '' ? props.allDataItem : selected;
    setSelected(item);
    await props.onSelect(props.name, item);
  };

  const handleCreate = async (item) => {
    await handleSelectAndConfirm(item);
    const filter = buildFilter(item, props.textField);
    setFilter(filter);
    setIsOpen(false);
    handleBlur();
  }

  const processFilter = (filter) => {
    let processedFilter = filter;
    if (fixedFilter) {
      processedFilter = { filters: [...filter.filters, ...fixedFilter.filters] };
    }
    if (props.initFilter) {
      processedFilter = { filters: [...processedFilter.filters, ...props.initFilter.filters] };
    }
    return processedFilter;
  };

  // const applyFilter = (id) => {
  //   props.onChange({ filters: [{ field: props.primaryField, operator: "eq", value: id }] }, [], { skip: 0, take: 1 });
  //   handleFilter({ filters: [{ field: props.primaryField, operator: "eq", value: id }] });
  //   if (props.onSubmit) {
  //     props.onSubmit();
  //   }
  //   eventEmitter.changeTab(props.primaryField, 0);
  // };

  const onChange = (filter, sort, page) => {
    if (filter) {
      props.onChange(filter, sort, page, props.primaryField);
    } else {
      let page = sessionStorage.Page ? JSON.parse(sessionStorage.Page) : [0, 5];
      props.onChange(processFilter(filter), "", page, props.primaryField);
      eventEmitter.changeTab(props.primaryField, 0);
    }
  };

  const handleBlur = () => {
    dataCheck();
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const dataCheck = () => {
    if (!props.selected || Object.keys(props.selected).length === 0) {
      setSelected(null);
    }
  };

  // const onAdd = async () => {
  //   if (props.editableDetail) {
  //     await props.onSubmit(0);
  //   }
  //   if (props.onAdd) {
  //     await props.onAdd();
  //   }
  //   eventEmitter.changeTab(props.primaryField, 1);
  // };

  // const onRowDoubleClick = async () => {
  //   if (props.editableDetail) {
  //     let id = selected[props.primaryField];
  //     await props.onSubmit(id);
  //     eventEmitter.changeTab(props.primaryField, 1);
  //   }
  // };

  // const getPrimaryField = () => {
  //   return getPrimaryField(props.primaryField);
  // };

  /**
  * Metoda buildFooter
  * vráti element, ktorý sa má vyrenderovať pod dropdownlistom
  * default zobrazuje počet zobrazených záznamov a celkým počet záznamov
  */


  const buildFooter = () => {
    let text = props.data && props.data.length > 0 ? `Zobrazených ${props.data.length} z ${props.total} záznamov` : "";
    return (
      <div className="autocomplete-footer" title="Pre zobrazenie nezobrazených výsledkov spresnite podmienku vyhľadávania alebo použite dialóg.">
        <div className="autocomplete-footer-text">
          {text}
        </div>
      </div>
    );
  };

  const handleReducer = async (action, data, state, frmState) => {
    let _state = { ...state, parentDetailData: props.parentProps?.detailData };
    switch (action) {
      case "GET_SCHEMA":
        state = _state;
        break;
      case "SELECT":
        handleSelect(_state[state.primaryField]?.selected);
        break;
      case "CREATE":
        await handleCreate(data.data);
        break;
      default:
        break;
    }
    if (props.reducer) {
      return await props.reducer(action, data, _state, frmState);
    } else {
      return _state;
    }
  };

  const gridDateColumns = props.schema.filter(x => isDateType(x.filter));
  const data = props.data.map(x => { return processAfter(gridDateColumns, x) });

  return (
    <div className={`form-input picker ${props.disabled ? "disabled-field" : props.required ? "required-field" : "default-field"}`}>
      <AutoComplete
        textField={props.textField}
        valueField={props.valueField}
        primaryField={props.primaryField}
        selected={props.selected}
        onChange={(filter, sort, page) => onChange(processFilter(filter), sort, page)}
        data={data}
        isLoading={props.isLoading}
        onSelect={handleSelectAndConfirm}
        onBlur={handleBlur}
        filter={filter}
        onFilter={handleFilter}
        required={props.required}
        disabled={props.disabled}
        validationMessage={props.validationMessage}
        autofocus={props.autofocus}
        placeholder={props.placeholder}
        tabIndex={props.tabIndex}
        footer={buildFooter()}
        allDataItem={props.allDataItem}
        sort={props.sort}
      />
      <Button
        type="button"
        onClick={handleOpen}
        icon="more-vertical"
        disabled={props.disabled}
      />
      {isOpen && <Dialog
        isOpen={isOpen}
        title={props.title}
        onClose={handleClose}
        onConfirm={handleConfirm}
        confirmDisabled={!selected}
      >
        {props.detail ?
          <MetaGrid
            primaryField={props.primaryField}
            componentFactory={() => { return null; }}
            akcFactory={props.akcFactory}
            reducer={handleReducer}
            initFilter={filter}
            fixedFilter={fixedFilter}
            db={props.db}
          />
          :
          <Grid
            total={props.total}
            data={data}
            isLoading={props.isLoading}
            onChange={onChange}
            schema={props.schema}
            toolbarHidden={true}
            filter={filter}
            onFilter={handleFilter}
            onSelect={handleSelect}
            primaryField={props.primaryField}
            onConfirm={handleConfirm}
            identif={props.identif}
            isPicker
            parentProps={props.parentProps}
            currentDataItem={props.currentDataItem}
            hidden={flags.PRINT | flags.RESET | flags.LEFT_TOOLBAR | flags.EDITCELL}
            withMeta={props.withMeta}
            fixedFilter={fixedFilter}
            db={props.db}
          />
        }
      </Dialog>}
    </div>
  );

}

PickerComponent.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  textField: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.object,
  total: PropTypes.number.isRequired,
  schema: PropTypes.array.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  primaryField: PropTypes.string,
  onOpened: PropTypes.func,
  //initFilter - filter, ktorý je clear-nuteľný
  initFilter: PropTypes.object,
  //fixedFilter - filter, ktorý nie je možné odstrániť
  fixedFilter: PropTypes.object,
  autofocus: PropTypes.bool,
  placeholder: PropTypes.string,

  //Vytvorenie nového záznamu v pickeri (null = bez tejto možnosti)
  detail: PropTypes.bool,
  reducer: PropTypes.func,
  onAdd: PropTypes.func,
  tabIndex: PropTypes.number,
  isOpen: PropTypes.bool,
  openPicker: PropTypes.func,
  closePicker: PropTypes.func,
  editableDetail: PropTypes.bool,
  withMeta: PropTypes.bool,
  parentProps: PropTypes.object,
  allDataItem: PropTypes.object,
  db: PropTypes.string,
  sort: PropTypes.array
}

export default withMeta(PickerComponent);